<template>
  <form @submit.prevent="handleSubmit">
    <div>
      <div>
        <form.Field
          name="date"
          :validators="{
            onBlur: yup.string().required('Date is required')
          }"
        >
          <template #default="{ field }">
            <BaseInputDate
              :model-value="(field.state.value as string)"
              :name="field.name"
              label="Date"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              @blur="field.handleBlur"
              @input="(e) => field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>
      </div>

      <div>
        <form.Field
          name="method"
          :validators="{
            onBlur: yup.string().required('Payment method is required')
          }"
        >
          <template #default="{ field }">
            <BaseSelect
              :model-value="(field.state.value as string)"
              :name="field.name"
              label="Payment Method"
              :options="paymentMethods"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              @blur="field.handleBlur"
              @input="(e) => field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>
      </div>

      <div>
        <form.Field
          name="amount"
          :validators="{
            onBlurListenTo: ['fullAmountChecked'],
            onBlur: yup.string().transform((value) => value.replace(/,/g, ''))
              .test('is-number','Amount must be a valid number and less than or equal to the invoice balance: '+currency(invoice.balance),(value) => testAmount(value))
            }"
        >
          <template #default="{ field }">
            <BaseInputCurrency
              :model-value="(field.state.value as string)"
              :disabled="fullAmount"
              :name="field.name"
              label="Amount"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              @blur="field.handleBlur"
              @input="(e) => field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>
      </div>

      <form.Field
        name="fullAmountChecked"
      >
        <template #default="{ field }">
          <BaseCheckbox
            v-model="fullAmount"
            label="Full Amount"
            @change="(e) => {field.handleChange(e.target.checked); field.handleBlur()}"
          />
        </template>
      </form.Field>

      <div>
        <form.Field
          name="memo"
          :validators="{
            onBlur: yup.string()
          }"
        >
          <template #default="{ field }">
            <Editor
              v-model="(field.state.value as string)"
              :additional-options="{ placeholder: 'Payment memo' }"
              @update:model-value="(value) => field.handleChange(value)"
            />
          </template>
        </form.Field>
      </div>
    </div>

    <form.Subscribe>
      <template #default="{ canSubmit, isSubmitting }">
        <div class="flex justify-end gap-2 mt-4">
          <BaseButton
            variant="base"
            type="button"
            text="Cancel"
            :disabled="isSubmitting || isPending"
            :class="{ 'cursor-not-allowed opacity-50': isSubmitting || isPending }"
            @click="$emit('cancel')"
          />

          <BaseButton
            variant="primary"
            text="Save"
            type="submit"
            :disabled="!canSubmit || isSubmitting || isPending"
            :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting || isPending }"
          >
            <div class="flex justify-between items-center gap-2">
              <span>Save</span>
              <span
                v-if="isSubmitting || isPending"
                class="i-heroicons-arrow-path animate-spin"
              />
            </div>
          </BaseButton>
        </div>

        </template>
    </form.Subscribe>
  </form>
</template>

<script lang="ts" setup>
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import BaseInputDate from '@/components/base/BaseInputDate.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseInputCurrency from '@/components/base/BaseInputCurrency.vue'
import Editor from '@/components/TextEditor.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { Invoice } from '@/models/invoice'
import { currency, toNumber } from '@/utils'
import { onMounted, ref, watch } from 'vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'

const props = defineProps<{
  modelValue: {
    date: string
    method: string
    amount: number
    memo: string
  }
  invoice: Invoice,
  isPending: boolean
}>()

const emit = defineEmits(['update:modelValue', 'save', 'cancel'])

const paymentMethods = [
  { value: 'Cash', label: 'Cash' },
  { value: 'Check', label: 'Check' },
  { value: 'Credit Card', label: 'Credit Card' },
  { value: 'Bank Transfer', label: 'Bank Transfer' }
]

const fullAmount = ref(false)

const form = useForm({
  defaultValues: {
    ...props.modelValue,
    fullAmountChecked: false
  },
  validatorAdapter: yupValidator(),
  onSubmit: ({ value }) => {
    const payment = {
      ...value,
      amount: toNumber(value.amount),
    }

    delete payment.fullAmountChecked
    emit('save', payment)
  }
})

watch(fullAmount, (newValue) => {
  if (newValue) {
    form.setFieldValue('amount', toNumber(props.invoice.balance))
  } else {
    form.setFieldValue('amount', 0)
  }
})

const handleSubmit = () => {
  form.handleSubmit()
}

const testAmount = (value: string | undefined) => {
  if (!value) return false
  const numValue = toNumber(value)
  if (props.invoice.balance > 0) {
    return !isNaN(numValue) && numValue <= toNumber(props.invoice.balance) && numValue > 0
  }
  return !isNaN(numValue) && numValue <= toNumber(props.invoice.balance)
}

onMounted(() => {
  form.reset()
})

</script>
